// Configurador del drill-down jerárquico para un geoportal.
// El usuario define una pila de niveles ordenada (e.g. depto → prov → dist).
// Cada nivel apunta a una capa + columna ID; los niveles 2+ además declaran
// la columna que joinea con el padre.
import { useEffect, useState } from 'react';
import { api, type Layer, type DrillConfig, type DrillLevel } from '../lib/api';

type Props = {
  geoportalId: string;
  layers: Layer[];
  initial: DrillConfig | null | undefined;
  onSaved: () => void;
};

export function DrillConfigPanel({ geoportalId, layers, initial, onSaved }: Props) {
  const [enabled, setEnabled] = useState(initial?.enabled ?? false);
  const [levels, setLevels]   = useState<DrillLevel[]>(initial?.levels ?? []);
  const [saving, setSaving]   = useState(false);

  // Cache de columnas por capa para los selects
  const [colsByLayer, setColsByLayer] = useState<Record<string, { name: string; type: string }[]>>({});

  useEffect(() => {
    // Pre-load columnas de todas las capas usadas en niveles
    const ids = new Set(levels.map(l => l.layerId).filter(Boolean));
    for (const id of ids) {
      const layer = layers.find(l => l.id === id);
      if (!layer || colsByLayer[id]) continue;
      api.listColumns(layer.sourceSchema, layer.sourceTable)
         .then(r => setColsByLayer(c => ({ ...c, [id]: r.columns })))
         .catch(() => {});
    }
  }, [levels, layers]);

  function addLevel() {
    setLevels(L => [...L, { layerId: '', idColumn: '', label: '' }]);
  }
  function removeLevel(i: number) {
    setLevels(L => L.filter((_, idx) => idx !== i));
  }
  function patchLevel(i: number, patch: Partial<DrillLevel>) {
    setLevels(L => L.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  }
  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= levels.length) return;
    setLevels(L => {
      const next = [...L];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  async function save() {
    setSaving(true);
    try {
      const validLevels = levels.filter(l => l.layerId && l.idColumn);
      const payload = enabled && validLevels.length > 0
        ? { enabled: true, levels: validLevels }
        : null;
      await api.updateGeoportal(geoportalId, { drillConfig: payload as any });
      onSaved();
    } finally { setSaving(false); }
  }

  return (
    <div style={{ marginTop: 12, padding: '10px 12px', background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
        <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
        <span>Drill-down jerárquico</span>
      </label>
      {enabled && (
        <>
          <p style={{ fontSize: 11, color: '#64748b', margin: '0 0 8px' }}>
            Click en una feature de un nivel oculta el nivel actual y muestra el siguiente filtrado por el padre.
          </p>
          {levels.map((lv, i) => {
            const layer = layers.find(l => l.id === lv.layerId);
            const cols = layer ? (colsByLayer[layer.id] ?? []) : [];
            return (
              <div key={i} style={{ marginBottom: 8, padding: 8, background: 'white', border: '1px solid #e2e8f0', borderRadius: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#475569', flex: 1 }}>Nivel {i + 1}</span>
                  <button onClick={() => move(i, -1)} disabled={i === 0} style={btnIcon}>↑</button>
                  <button onClick={() => move(i, +1)} disabled={i === levels.length - 1} style={btnIcon}>↓</button>
                  <button onClick={() => removeLevel(i)} style={{ ...btnIcon, color: '#dc2626' }}>×</button>
                </div>
                <select value={lv.layerId} onChange={e => patchLevel(i, { layerId: e.target.value, idColumn: '', parentColumn: undefined })} style={{ ...inp, marginBottom: 4 }}>
                  <option value="">— capa —</option>
                  {layers.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
                {layer && (
                  <>
                    <select value={lv.idColumn} onChange={e => patchLevel(i, { idColumn: e.target.value })} style={{ ...inp, marginBottom: 4 }}>
                      <option value="">— columna ID —</option>
                      {cols.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                    </select>
                    {i > 0 && (
                      <select value={lv.parentColumn ?? ''} onChange={e => patchLevel(i, { parentColumn: e.target.value || undefined })} style={{ ...inp, marginBottom: 4 }}>
                        <option value="">— columna que joinea con padre —</option>
                        {cols.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                      </select>
                    )}
                    <input value={lv.label ?? ''} onChange={e => patchLevel(i, { label: e.target.value })} placeholder={`Label (e.g. ${i === 0 ? 'Departamento' : i === 1 ? 'Provincia' : 'Distrito'})`} style={inp} />
                  </>
                )}
              </div>
            );
          })}
          <button onClick={addLevel} style={{ fontSize: 11, padding: '4px 8px', borderRadius: 4, border: '1px dashed #cbd5e1', background: 'white', cursor: 'pointer', width: '100%', marginBottom: 6 }}>
            + Agregar nivel
          </button>
        </>
      )}
      <button onClick={save} disabled={saving} style={{ fontSize: 12, padding: '6px 10px', background: '#0ea5e9', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', width: '100%' }}>
        {saving ? 'Guardando…' : 'Guardar drill-down'}
      </button>
    </div>
  );
}

const inp: React.CSSProperties = { width: '100%', padding: '4px 6px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 4, boxSizing: 'border-box', background: 'white' };
const btnIcon: React.CSSProperties = { width: 22, height: 22, padding: 0, border: '1px solid #cbd5e1', background: 'white', borderRadius: 4, fontSize: 11, cursor: 'pointer' };
